import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const AdminMatchingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { admin } = useAuth();

  useEffect(() => {
    if (admin && admin.role !== 'super_admin') {
      navigate(`/admin/events/${id}`);
    }
  }, [admin, id]);

  const [event, setEvent] = useState(null);
  const [threshold, setThreshold] = useState(45);
  const [running, setRunning] = useState(false);
  const [runStatus, setRunStatus] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`/api/admin/events/${id}`)
      .then(res => setEvent(res.data))
      .catch(() => navigate('/admin/events'));

    // Check if already running
    checkStatus();
    return () => clearInterval(pollRef.current);
  }, [id]);

  const checkStatus = async () => {
    try {
      const res = await axios.get(`/api/admin/events/${id}/match/status`);
      setRunStatus(res.data);
      if (res.data.status === 'running') {
        setRunning(true);
        startPolling();
      }
    } catch (_) {}
  };

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`/api/admin/events/${id}/match/status`);
        setRunStatus(res.data);
        if (res.data.status !== 'running') {
          clearInterval(pollRef.current);
          setRunning(false);
          if (res.data.status === 'completed') {
            setSuccess(`✅ Matching complete! Found ${res.data.total_matches} matches across all visitors.`);
          } else if (res.data.status === 'failed') {
            setError(`Matching failed: ${res.data.error_message}`);
          }
        }
      } catch (_) {}
    }, 2000); // Poll every 2 seconds
  };

  const handleStart = async () => {
    setError('');
    setSuccess('');
    setRunning(true);
    try {
      const res = await axios.post(`/api/admin/events/${id}/match`, { threshold });
      setRunStatus({ status: 'running', total_photos: res.data.total_photos, processed_photos: 0, progress_percent: 0 });
      startPolling();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start matching.');
      setRunning(false);
    }
  };

  const getThresholdLabel = (t) => {
    if (t >= 65) return { label: 'Strict', color: '#7c3aed', desc: 'Only high-confidence matches. May miss some real matches in crowd photos.' };
    if (t >= 50) return { label: 'Balanced', color: '#16a34a', desc: 'Good balance of accuracy and coverage for event photos.' };
    if (t >= 40) return { label: 'Recommended', color: '#16a34a', desc: 'Best for event photos — ArcFace similarity for crowd shots typically falls in this range.' };
    return { label: 'Very Lenient', color: '#d97706', desc: 'Most matches shown. May include some false positives.' };
  };

  const tl = getThresholdLabel(threshold);
  const progress = runStatus?.progress_percent || 0;

  return (
    <div style={styles.page}>
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
        <button style={styles.backBtn} onClick={() => navigate(`/admin/events/${id}`)}>← Back to Event</button>
        <span style={{ fontWeight: 700, color: '#1a1a2e' }}>Face Matching</span>
      </div>

      <div style={styles.container}>
        {event && (
          <div style={styles.eventBanner}>
            <strong>{event.name}</strong>
            <span style={styles.eventDate}>
              {new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        )}

        <div style={styles.grid}>
          {/* Threshold Card */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Matching Threshold</h2>
            <p style={styles.cardDesc}>
              Set how similar a face must be to a registered selfie before it counts as a match.
            </p>

            <div style={styles.thresholdDisplay}>
              <span style={{ ...styles.thresholdValue, color: tl.color }}>{threshold}%</span>
              <span style={{ ...styles.thresholdLabel, color: tl.color }}>{tl.label}</span>
            </div>

            <input
              type="range"
              min="40"
              max="95"
              step="1"
              value={threshold}
              onChange={e => setThreshold(Number(e.target.value))}
              disabled={running}
              style={styles.slider}
            />

            <div style={styles.sliderLabels}>
              <span>← More Matches</span>
              <span>Fewer Matches →</span>
            </div>

            <div style={{ ...styles.thresholdHint, borderColor: tl.color + '40', background: tl.color + '10' }}>
              <p style={{ margin: 0, color: tl.color, fontSize: '0.875rem' }}>{tl.desc}</p>
            </div>

            {/* Presets */}
            <div style={styles.presets}>
              {[
                { label: 'Strict (65%)', value: 65 },
                { label: 'Balanced (50%)', value: 50 },
                { label: 'Recommended (40%)', value: 40 }
              ].map(p => (
                <button
                  key={p.value}
                  style={{ ...styles.presetBtn, ...(threshold === p.value ? styles.presetActive : {}) }}
                  onClick={() => setThreshold(p.value)}
                  disabled={running}
                >
                  {p.label} ({p.value}%)
                </button>
              ))}
            </div>
          </div>

          {/* Run Card */}
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Run Matching</h2>

            {!running && !runStatus?.status && (
              <p style={styles.cardDesc}>
                This will compare every visitor's selfie against all event photos. 
                This process cannot be interrupted once started but you can re-run with different settings.
              </p>
            )}

            {error && <div style={styles.errorBox}>{error}</div>}
            {success && <div style={styles.successBox}>{success}</div>}

            {/* Progress */}
            {(running || runStatus?.status === 'completed' || runStatus?.status === 'failed') && runStatus && (
              <div style={styles.progressSection}>
                <div style={styles.progressHeader}>
                  <span style={styles.progressLabel}>
                    {running ? 'Processing photos...' : runStatus.status === 'completed' ? 'Completed' : 'Failed'}
                  </span>
                  <span style={styles.progressPct}>{progress}%</span>
                </div>
                <div style={styles.progressBar}>
                  <div style={{ ...styles.progressFill, width: `${progress}%`, background: runStatus.status === 'failed' ? '#dc2626' : '#1a1a2e' }} />
                </div>
                <div style={styles.progressStats}>
                  <span>Photos: {runStatus.processed_photos || 0} / {runStatus.total_photos || 0}</span>
                  <span>Matches so far: {runStatus.total_matches || 0}</span>
                </div>
                {running && (
                  <p style={{ fontSize: '0.8rem', color: '#888', margin: '0.5rem 0 0', textAlign: 'center' }}>
                    This may take several minutes for large events. Page will update automatically.
                  </p>
                )}
              </div>
            )}

            <button
              style={{ ...styles.startBtn, ...(running ? styles.startBtnDisabled : {}) }}
              onClick={handleStart}
              disabled={running}
            >
              {running ? (
                <span style={styles.spinnerRow}>
                  <span style={styles.spinner} /> Processing...
                </span>
              ) : runStatus?.status === 'completed' ? '🔄 Re-run with New Threshold' : '🧠 Start Face Matching'}
            </button>

            {runStatus?.status === 'completed' && (
              <button
                style={styles.viewGalleryBtn}
                onClick={() => navigate(`/admin/events/${id}`)}
              >
                ← Back to Event Dashboard
              </button>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div style={styles.infoBox}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#1a1a2e' }}>ℹ️ How matching works</h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', lineHeight: '1.6' }}>
            Each visitor's registered selfie is compared to every face detected in every event photo using 
            ArcFace deep learning. A photo is added to a visitor's album only when the face similarity 
            score meets or exceeds the threshold you set. Running matching again with a new threshold 
            clears previous results and creates a fresh album for all visitors.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  nav: { background: '#1a1a2e', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  backBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem' },
  navTitle: { color: '#fff', fontWeight: '700', fontSize: '1.05rem' },
  container: { maxWidth: '900px', margin: '0 auto', padding: '2rem 1.5rem' },
  eventBanner: { background: '#fff', borderRadius: '1rem', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb', fontWeight: '700', color: '#1a1a2e' },
  eventDate: { color: '#888', fontWeight: '400', fontSize: '0.875rem' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' },
  card: { background: '#fff', borderRadius: '1.5rem', padding: '1.75rem', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  cardTitle: { fontSize: '1.1rem', fontWeight: '800', color: '#1a1a2e', margin: '0 0 0.5rem' },
  cardDesc: { fontSize: '0.875rem', color: '#666', margin: '0 0 1.5rem', lineHeight: '1.5' },
  thresholdDisplay: { display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '1rem' },
  thresholdValue: { fontSize: '3rem', fontWeight: '800', lineHeight: 1 },
  thresholdLabel: { fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' },
  slider: { width: '100%', marginBottom: '0.5rem', cursor: 'pointer' },
  sliderLabels: { display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#bbb', marginBottom: '1rem' },
  thresholdHint: { borderRadius: '0.75rem', padding: '0.75rem', border: '1px solid', marginBottom: '1.25rem' },
  presets: { display: 'flex', gap: '0.5rem' },
  presetBtn: { flex: 1, padding: '0.5rem 0.25rem', background: '#f3f4f6', border: '1.5px solid transparent', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', color: '#666' },
  presetActive: { background: '#eff6ff', borderColor: '#3b82f6', color: '#1e40af' },
  progressSection: { marginBottom: '1.5rem' },
  progressHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' },
  progressLabel: { fontSize: '0.875rem', fontWeight: '600', color: '#444' },
  progressPct: { fontSize: '0.875rem', fontWeight: '800', color: '#1a1a2e' },
  progressBar: { height: '10px', background: '#f3f4f6', borderRadius: '999px', overflow: 'hidden', marginBottom: '0.5rem' },
  progressFill: { height: '100%', borderRadius: '999px', transition: 'width 0.5s ease' },
  progressStats: { display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#888' },
  startBtn: { width: '100%', padding: '0.875rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: '700', fontSize: '1rem', cursor: 'pointer', marginBottom: '0.75rem' },
  startBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  viewGalleryBtn: { width: '100%', padding: '0.75rem', background: '#f3f4f6', color: '#444', border: 'none', borderRadius: '0.75rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.75rem', padding: '0.75rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: '1rem' },
  successBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.75rem', padding: '0.75rem', color: '#166534', fontSize: '0.875rem', marginBottom: '1rem' },
  infoBox: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '1rem', padding: '1.25rem' },
  spinnerRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' },
  spinner: { width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }
};

export default AdminMatchingPage;
